import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAutocomplete } from '../../features/products/api';

/**
 * Search box with fuzzy autocomplete (SRS §4.2, §4.3). Input is debounced 300ms
 * before hitting the pg_trgm endpoint. Enter runs a full-text search; picking a
 * suggestion jumps straight to that product.
 */
export function SearchBar({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const navigate = useNavigate();
  const listId = useId();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 300);
    return () => clearTimeout(t);
  }, [term]);

  const { data: hits = [] } = useAutocomplete(debounced);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function submitSearch(q: string) {
    if (!q.trim()) return;
    setOpen(false);
    onNavigate?.();
    navigate(`/products?q=${encodeURIComponent(q.trim())}`);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || hits.length === 0) {
      if (e.key === 'Enter') submitSearch(term);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[active];
      if (hit) {
        setOpen(false);
        onNavigate?.();
        navigate(`/products/${hit.slug}`);
      } else {
        submitSearch(term);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={boxRef} className="relative w-full">
      <div className="flex items-center rounded-full border border-frost bg-cloud focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-500/15">
        <svg className="ml-3 shrink-0" width="18" height="18" viewBox="0 0 20 20" aria-hidden>
          <path
            d="M9 3a6 6 0 104.47 10.03l3.75 3.75 1.06-1.06-3.75-3.75A6 6 0 009 3zm0 1.5a4.5 4.5 0 110 9 4.5 4.5 0 010-9z"
            fill="var(--color-neutral)"
          />
        </svg>
        <input
          type="search"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search products or brands…"
          aria-label="Search products"
          role="combobox"
          aria-expanded={open && hits.length > 0}
          aria-controls={open && hits.length > 0 ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
          className="min-w-0 w-full bg-transparent px-3 py-2.5 text-base md:text-sm text-navy-900 placeholder:text-neutral focus:outline-none"
        />
        <button
          type="button"
          aria-label="Submit search"
          onClick={() => submitSearch(term)}
          className="mr-1 flex min-h-9 min-w-11 shrink-0 items-center justify-center rounded-full bg-navy-900 px-3 text-white hover:bg-navy-700"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            aria-hidden="true"
          >
            <path d="M4 10h12m-5-5 5 5-5 5" />
          </svg>
        </button>
      </div>

      {open && hits.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-frost bg-white shadow-hover"
        >
          {hits.map((hit, i) => (
            <li id={`${listId}-${i}`} key={hit.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  setOpen(false);
                  onNavigate?.();
                  navigate(`/products/${hit.slug}`);
                }}
                className={`block w-full px-4 py-2.5 text-left text-sm text-navy-900 ${
                  i === active ? 'bg-cloud' : ''
                }`}
              >
                {hit.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
